/**
 * The one state a lost post reply can be *resolved* out of, and the rule that
 * decides it.
 *
 * **RULED by Copeland, 2026-08-24**, closing the question D6 left open. A post whose
 * reply never arrived leaves two possibilities that need opposite answers:
 *
 * - The request **arrived** and posting started, then stalled — `presumed_stuck`.
 *   D-5 is unchanged there: report it, say to contact Copeland, and never re-offer.
 *   Whether a half-finished post counts as posted is a question about the ledger.
 * - The request **never arrived**. Nothing was staged for posting, nothing is in the
 *   ledger, and a manager is holding a counted shelf they cannot finish.
 *
 * Business Central separates them itself. `PostSession` marks a session `Posting`
 * **as its first act** under the row lock — before it validates anything — so a
 * session still reading `InReview` a full minute after the attempt is one the
 * posting codeunit never took. Sixty seconds is not an estimate of that latency; it
 * is three orders of magnitude past it.
 *
 * Two conditions, and the rule needs both:
 *
 * 1. **At least {@link NEVER_STARTED_MIN_SECONDS} seconds** since
 *    `post_attempted_at`, measured on each reading rather than once.
 * 2. **{@link NEVER_STARTED_MIN_POLLS} consecutive** polls that each read
 *    `InReview` inside that window.
 *
 * ⚠ **What this permits is not a retry.** The lost request is never re-sent, by this
 * module or any other — nothing is queued, replayed or resumed, and the client still
 * carries `retry: 0` on the post. What comes back is the **confirm modal**: the same
 * gesture, the same `expected_line_count`, made again by a manager who has been told
 * in as many words that the first attempt never reached Business Central. The
 * asymmetry the ruling weighs is that a double-post is visible, has a document
 * number and is corrected by posting another count, while a manager stranded on a
 * Friday is the outcome this app exists to prevent — and `PostSession`'s own
 * `InReview` guard refuses the second post anyway if the first one did land.
 *
 * The rule is **pure and mirrored**: `count/posting.py`'s `never_started_reoffer`
 * folds the same conditions over a poll history for the live verification script,
 * and `tests/test_frontend_contract.py` pins the two numbers against each other.
 */
import { useEffect, useState } from "react";

/** Mirrors `count/posting.py` `NEVER_STARTED_MIN_SECONDS`. */
export const NEVER_STARTED_MIN_SECONDS = 60;

/** Mirrors `count/posting.py` `NEVER_STARTED_MIN_POLLS`. */
export const NEVER_STARTED_MIN_POLLS = 2;

/**
 * The only status this rule reads as "never started".
 *
 * Not `Open`, which shares the not-started *card* on the outcome screen: a count
 * that is `Open` was never submitted for review, `PostSession` refuses it outright,
 * and re-offering a post there would offer something the server would reject. This
 * is `POSTABLE_STATUS` in `count/posting.py` — the one status a post can go from,
 * which is exactly the one a never-delivered post would have left behind.
 */
export const NEVER_STARTED_STATUS = "InReview";

/** What one poll saw. */
export interface PollObservation {
  /** `post_attempted_at`, the marker the whole verifying state hangs on. */
  postAttemptedAt: string | null;
  /** When this poll answered, in epoch ms — react-query's own stamp for it. */
  observedAt: number;
  /** The status Business Central reported. */
  status: string | undefined;
  /** Whether the poll itself failed, in which case it observed nothing. */
  failed: boolean;
}

/** A running count of consecutive qualifying readings, and what it was counting. */
export interface PollTally {
  /**
   * The marker the streak belongs to. A new post attempt writes a new
   * `post_attempted_at`, and the evidence about the old one says nothing about the
   * new one — so the streak starts over rather than carrying across.
   */
  marker: string | null;
  /** The stamp of the last reading folded in, so the same one is not counted twice. */
  observedAt: number;
  /** Consecutive qualifying readings. */
  count: number;
}

export const NO_POLLS: PollTally = { marker: null, observedAt: 0, count: 0 };

/**
 * Whether one reading is evidence that Business Central never took the post.
 *
 * A reading from inside the first minute is not — not even as a partial. That is the
 * "measured on each reading" half of the ruling, and it is what makes the streak
 * impossible to assemble out of readings taken before the window opened.
 */
export function qualifies(observation: PollObservation): boolean {
  const { postAttemptedAt, observedAt, status, failed } = observation;
  if (failed || status !== NEVER_STARTED_STATUS || !postAttemptedAt) return false;
  const attempted = Date.parse(postAttemptedAt);
  if (Number.isNaN(attempted)) return false;
  // `>=`, and never negative: a marker stamped in the future is a clock that moved,
  // and a clock that moved is not sixty seconds of evidence.
  return observedAt - attempted >= NEVER_STARTED_MIN_SECONDS * 1000;
}

/**
 * Fold one poll into the tally.
 *
 * Anything that is not a qualifying reading **resets** the streak — a `Posting`, a
 * failed poll, a reading from inside the window. "Consecutive" is the word the
 * ruling uses, and a streak that survived contrary evidence would not be one.
 */
export function tallyPoll(previous: PollTally, observation: PollObservation): PollTally {
  const marker = observation.postAttemptedAt;
  const base = marker === previous.marker ? previous : { marker, observedAt: 0, count: 0 };
  // No answer yet (or the same answer again): the tally is unchanged, but the marker
  // it belongs to is recorded, so the first real reading counts as the first.
  if (observation.observedAt === 0 || observation.observedAt === base.observedAt) return base;
  return {
    marker,
    observedAt: observation.observedAt,
    count: qualifies(observation) ? base.count + 1 : 0,
  };
}

/** Whether the confirm may be put back in front of the manager. */
export function mayReofferPost(tally: PollTally): boolean {
  return tally.count >= NEVER_STARTED_MIN_POLLS;
}

/**
 * The tally for a session's poll, kept across renders.
 *
 * Driven by react-query's `dataUpdatedAt`/`errorUpdatedAt` rather than by the data
 * itself, because **two consecutive polls that both say `InReview` are two identical
 * answers** — structural sharing hands back the same object, and a hook watching the
 * value would see one reading no matter how long it went on. The stamps are what
 * distinguish a second poll from a second render.
 */
export function useNeverStartedTally(poll: {
  postAttemptedAt: string | null;
  status: string | undefined;
  dataUpdatedAt: number;
  errorUpdatedAt: number;
}): PollTally {
  const [tally, setTally] = useState<PollTally>(NO_POLLS);
  const { postAttemptedAt, status, dataUpdatedAt, errorUpdatedAt } = poll;
  // The later of the two: a failed refetch leaves `dataUpdatedAt` on the last
  // *successful* answer, so watching data alone would miss the failure entirely and
  // keep counting a streak through an outage.
  const failed = errorUpdatedAt > dataUpdatedAt;
  const observedAt = Math.max(dataUpdatedAt, errorUpdatedAt);

  useEffect(() => {
    setTally((previous) =>
      tallyPoll(previous, { postAttemptedAt, observedAt, status, failed }),
    );
  }, [failed, observedAt, postAttemptedAt, status]);

  return tally;
}
